// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { getIsWebCrawler } from "@insite/client-framework/Components/ContentItemStore";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    DispatchLoadProducts,
    GetUnfilteredProducts,
    HandleSortOrderDefault,
    LoadRealTimeInventory,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import loadRegionalInventory from "@mcmc/App/Handlers/CommonHandlers/LoadRegionalInventory";
import setCustomProductState from "@mcmc/App/Handlers/Pages/ProductDetails/SetCustomProductState";
import { parseProductCustomState } from "@mcmc/App/Handlers/Products/LoadProductByPath";
import { CustomProductState } from "@mcmc/App/Store/Reducers";
import { isEventSearchType } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { canUseDOM } from "exenv";

const productListSortTypeCookie = "productListSortType";

type HandlerType = Parameters<typeof PopulateApiParameter>[0];
export const AddSearchTypeAndExpandEventProperties = (props: HandlerType) => {
    const state = props.getState();
    const catalogPage = props.result.catalogPage;
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    if (category?.properties.isEventsCategory?.toLowerCase() === "true" || isEventSearchType(state)) {
        props.apiParameter.expand = [
            ...(props.apiParameter.expand || []),
            "properties",
            "content",
            "expandEvents",
        ] as any;
        (props.apiParameter as any).searchType = "event";
        (props.result.productFilters as any).searchType = "event";

        const isParentCategory = catalogPage && catalogPage.properties.hasSubCategories?.toLowerCase() === "true";
        if (!props.parameter.isSearch && isParentCategory) {
            (props.apiParameter as any).includeProductsInSubCategories = "true";
        }
    } else {
        (props.apiParameter as any).searchType = "product";
        (props.result.productFilters as any).searchType = "product";
    }
    const isStockItemsByDefault =
        getSession(state)?.properties?.filtrationNormallyStockedProducts?.toLowerCase() === "true";
    const stockItemsFilter = props.result.productFilters.stockedItemsOnly;
    /**
     * if stockItemsFilter === undefined (still hold the initial value) pick the value from account settings.
     * else pick the filter value
     */
    (props.apiParameter as any).stockedItemsOnly = stockItemsFilter ?? isStockItemsByDefault;
    (props.result.productFilters as any).stockedItemsOnly = stockItemsFilter ?? isStockItemsByDefault;
};

export const CustomHandleSortOrderDefault = (props: HandlerType) => {
    if (!props.apiParameter.sort) {
        props.apiParameter.sort = getCookie(productListSortTypeCookie);
    } else if (canUseDOM) {
        setCookie(productListSortTypeCookie, props.apiParameter.sort);
    }
};

export const DispatchLoadProductsCustom = (props: HandlerType) => {
    const customProductStates: CustomProductState[] = [];
    props.result.products?.every(product => {
        customProductStates.push(parseProductCustomState(product));
        return true;
    });
    props.dispatch(setCustomProductState({ customProductStates }));
};

export const CustomGetUnfilteredProducts = async (props: HandlerType) => {
    const unfilteredApiParameter = { ...props.apiParameter };
    delete unfilteredApiParameter.searchWithin;
    if (!props.result.productFilters.pageBrandId) {
        delete unfilteredApiParameter.brandIds;
    }
    if (!props.result.productFilters.pageProductLineId) {
        delete unfilteredApiParameter.productLineIds;
    }
    delete unfilteredApiParameter.priceFilters;
    delete unfilteredApiParameter.attributeValueIds;
    if (!props.result.productFilters.pageCategoryId) {
        delete unfilteredApiParameter.categoryId;
    }
    delete unfilteredApiParameter.stockedItemsOnly;
    delete unfilteredApiParameter.previouslyPurchasedProducts;

    props.result.unfilteredApiParameter = unfilteredApiParameter;
    props.result.filteredApiParameter = { ...props.apiParameter };

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;

    if (unfilteredProducts) {
        return;
    }

    await executeAwaitableHandlerChain(loadProducts, unfilteredApiParameter, props);
};

export const LoadRegionalInventory: typeof LoadRealTimeInventory = props => {
    const state = props.getState();
    const catalogPage = props.result.catalogPage;
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    if (
        !props.result.products?.length ||
        category?.properties.isEventsCategory?.toLowerCase() === "true" ||
        isEventSearchType(state) ||
        getIsWebCrawler()
    ) {
        return;
    }

    props.dispatch(
        loadRegionalInventory({
            productIds: props.result.products.map(o => o.id),
        }),
    );
};

replaceInChain(chain, HandleSortOrderDefault, CustomHandleSortOrderDefault); // Fix an OOTB issue with setCookies not allowed in serverside.
addToChainAfter(chain, PopulateApiParameter, AddSearchTypeAndExpandEventProperties);
addToChainAfter(chain, DispatchLoadProducts, DispatchLoadProductsCustom);
replaceInChain(chain, GetUnfilteredProducts, CustomGetUnfilteredProducts);
addToChainAfter(chain, LoadRealTimeInventory, LoadRegionalInventory);
